import { useLazyQuery } from '@apollo/client';
import { captureException } from '@sentry/react';
import { format } from 'date-fns';
import csvDownload from 'json-to-csv-export';
import { useCallback } from 'react';
import { REPORT_TYPES } from './constants';
import { MARKETPLACE_ORDERS_BILLING } from './queries';

export const useReportCallbacks = (state, callbacks) => {
    const [queryMarketplaceOrdersBilling] = useLazyQuery(MARKETPLACE_ORDERS_BILLING);

    const generateReport = useCallback(() => {
        let executor;
        switch (state.reportType) {
            case REPORT_TYPES.MARKETPLACE_ORDERS_BILLING:
                executor = queryMarketplaceOrdersBilling;
                break;
        }

        if (!executor) return;

        const variables = {
            where: {
                _and: [
                    ...state.report.static.map((parameter) => ({
                        [parameter.attribute]: { [parameter.comparator]: parameter.value },
                    })),
                    ...state.report.parameters
                        .filter((parameter) => {
                            const raw = state.parameters[parameter.key];
                            return Array.isArray(raw) ? raw.length > 0 : !!raw;
                        })
                        .map((parameter) => {
                            const raw = state.parameters[parameter.key];
                            const value = parameter.modifier ? parameter.modifier(raw) : raw;
                            return { [parameter.attribute]: { [parameter.comparator]: value } };
                        }),
                ],
            },
        };

        callbacks.setLoading((prev) => ({ ...prev, generate: true }));

        return executor({
            variables,
            fetchPolicy: 'network-only',
            onError: (e) => {
                console.error(e);
                captureException(e);
                callbacks.setNotification({
                    severity: 'error',
                    message: `Error: ${e.message}`,
                });
            },
            onCompleted: (data) => {
                const results = data?.results || [];
                callbacks.setData(results);
                callbacks.setNotification({
                    severity: 'success',
                    message: `Report Generated - ${results.length} results`,
                });
            },
        }).finally(() => {
            callbacks.setLoading((prev) => ({ ...prev, generate: false }));
        });
    }, [state.reportType, state.report, state.parameters, callbacks]);

    const exportReport = useCallback(() => {
        const columns = state.report.columns;
        const headers = columns.map((column) => column.Header);
        const data = state.data.map((row) => columns.map((column) => column.accessor(row)));
        const footer = columns.map((column) => column.footerAccessor(state.data));

        csvDownload({
            headers,
            data: [...data, footer],
            filename: `${state.reportType}-${format(new Date(), 'yyyy-MM-dd')}.csv`,
            delimiter: ',',
        });
    }, [state.reportType, state.report, state.data]);

    return {
        generateReport,
        exportReport,
    };
};
